import { LoadingSpinner } from "@/shared/components/feedback/LoadingSpinner";
import { DataTableColumnHeader } from "@/shared/components/table";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { formatDate } from "@/shared/libs/date";
import type { ColumnDef, Table } from "@tanstack/react-table";
import { useMemo } from "react";
import type { CollectionsE2EAssetRow, E2ETestHistoryRow } from "../types/collection-types";

function getSelectableRows(table: Table<CollectionsE2EAssetRow>) {
    return table.getRowModel().rows.filter((r) => r.original.asset_status === "ON" && r.original.can_run_test);
}

export function useCollectionsE2EColumns() {
    return useMemo<ColumnDef<CollectionsE2EAssetRow>[]>(
        () => [
            {
                id: "select",
                header: ({ table }) => {
                    const selectable = getSelectableRows(table);
                    const selectedCount = selectable.filter((r) => r.getIsSelected()).length;

                    const checked =
                        selectable.length > 0 &&
                        (selectedCount === selectable.length
                            ? true
                            : selectedCount > 0
                                ? "indeterminate"
                                : false);

                    return (
                        <Checkbox
                            checked={checked}
                            onCheckedChange={(v) => {
                                const next = !!v;
                                selectable.forEach((r) => r.toggleSelected(next));
                            }}
                            aria-label="Select all"
                        />
                    );
                },
                cell: ({ row }) => {
                    const enabled = row.original.asset_status === "ON" && row.original.can_run_test;
                    return (
                        <Checkbox
                            checked={row.getIsSelected()}
                            disabled={!enabled}
                            onCheckedChange={(v) => row.toggleSelected(!!v)}
                            aria-label="Select row"
                            onClick={(e) => e.stopPropagation()}
                        />
                    );
                },
                size: 50,
                minSize: 50,
                maxSize: 50,
                enableSorting: false,
                enableHiding: false,
                enableResizing: false,
            },
            {
                accessorKey: "status_circle",
                header: ({ column }) => <DataTableColumnHeader column={column} title="상태" />,
                cell: ({ row }) =>
                    row.original.status_circle === "loading" ? <LoadingSpinner size="sm" /> : row.original.status_circle,
                size: 60,
                minSize: 60,
                maxSize: 60,
                enableResizing: false,
            },
            { accessorKey: "channel", header: ({ column }) => <DataTableColumnHeader column={column} title="채널" /> },
            { accessorKey: "asset_name", header: ({ column }) => <DataTableColumnHeader column={column} title="자산명" /> },
            {
                accessorKey: "asset_status",
                header: ({ column }) => <DataTableColumnHeader column={column} title="자산 상태" />,
            },
            {
                accessorKey: "dw_account_id",
                header: ({ column }) => <DataTableColumnHeader column={column} title="DW Account ID" />,
            },
            {
                accessorKey: "last_executed_at",
                header: ({ column }) => <DataTableColumnHeader column={column} title="마지막 수행 시간" />,
                cell: ({ row }) => (row.original.last_executed_at ? formatDate(row.original.last_executed_at, "datetime") : "-"),
            },
            {
                accessorKey: "recent_circles_text",
                header: ({ column }) => <DataTableColumnHeader column={column} title="최근 5회 결과" />,
                cell: ({ row }) => <span className="font-mono">{row.original.recent_circles_text}</span>,
            },
            {
                accessorKey: "error_msg_preview",
                header: ({ column }) => <DataTableColumnHeader column={column} title="에러 메시지" />,
                cell: ({ row }) => (
                    <div className="truncate">{row.original.error_msg_preview || "-"}</div>
                ),
            },
            {
                accessorKey: "fail_desc_preview",
                header: ({ column }) => <DataTableColumnHeader column={column} title="오류 원인" />,
                cell: ({ row }) => (
                    <div className="truncate">{row.original.fail_desc_preview || "-"}</div>
                ),
            },
        ],
        []
    );
}

export function useCollectionsE2EHistoryColumns() {
    return useMemo<ColumnDef<E2ETestHistoryRow>[]>(
        () => [
            { accessorKey: "trans_id", header: ({ column }) => <DataTableColumnHeader column={column} title="Trans ID" /> },
            {
                accessorKey: "executed_at",
                header: ({ column }) => <DataTableColumnHeader column={column} title="수행 시간" />,
                cell: ({ row }) => formatDate(row.original.executed_at, "datetime"),
            },
            { accessorKey: "result_text", header: ({ column }) => <DataTableColumnHeader column={column} title="수행 결과" /> },
            {
                accessorKey: "processed_file_count",
                header: ({ column }) => <DataTableColumnHeader column={column} title="처리된 파일 수" />,
            },
            {
                accessorKey: "error_msg",
                header: ({ column }) => <DataTableColumnHeader column={column} title="에러 메시지" />,
                cell: ({ row }) => <div className="min-w-[360px] whitespace-normal break-words">{row.original.error_msg || "-"}</div>,
            },
        ],
        []
    );
}